import React, { useEffect, useRef, useState } from 'react';

const labelCls = 'block text-xs font-medium text-slate-500 dark:text-slate-400 mb-1';
const inputCls = 'w-full px-2.5 py-1.5 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 text-slate-800 dark:text-slate-100 text-sm';
const helperCls = 'text-[11px] text-slate-500 dark:text-slate-400 mt-1';
const errorCls = 'text-[11.5px] text-red-600 dark:text-red-400 mt-1';
const readonlyValueCls = 'text-[13px] text-slate-800 dark:text-slate-100';
// FIX-6 chip (replaces the FIX-3 bare-glyph toggle — owner's live-test bug
// report: "not clear how to go to the next line; not clear how to give a
// bullet and how not"). A labelled chip button, solid backgrounds both
// states per the CLAUDE.md button-visibility rule: teal filled + white text
// when ON ("• Bullet"), solid grey/white when OFF ("Plain").
const chipBaseCls = 'shrink-0 px-2 py-1 rounded-md border text-xs font-medium leading-none whitespace-nowrap transition-colors';
const chipOnCls = 'bg-teal-600 dark:bg-teal-600 border-teal-700 dark:border-teal-500 text-white';
const chipOffCls = 'bg-slate-100 dark:bg-slate-700 border-slate-400 dark:border-slate-500 text-slate-800 dark:text-slate-100';
// FIX-6 remove button — solid background, right of each row.
const removeBtnCls = 'shrink-0 w-6 h-6 rounded-md border border-slate-400 dark:border-slate-500 bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-100 text-sm leading-none flex items-center justify-center';
// FIX-6 "+ Add line" secondary button — solid per button-visibility rule.
const addLineBtnCls = 'mt-1.5 px-2.5 py-1 rounded-md border border-slate-400 dark:border-slate-500 bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-100 text-xs font-medium';
const readonlyBulletCls = 'w-4 shrink-0 text-slate-500 dark:text-slate-400 select-none text-center';
const readonlyPlainCls = 'w-4 shrink-0 select-none text-center';

// FIX-6 (DISCHARGE-TEMPLATES Phase 1 · owner's live-test bug report — rows
// showed a bare "•"/"–" toggle and nothing else: "not clear how to go to the
// next line; not clear how to give a bullet and how not") — replaces the
// separate FIX-2 "one medicine per line" line AND the FIX-3 shortcut line
// with one explicit sentence covering both the next-line affordance and the
// bullet/plain chip. Ratified copy kept as its own exported constant, single
// source, so it can't drift from what the field actually renders (same
// pattern as the FIX-2/FIX-3 constants it replaces).
// verdict-r2.md ruling C-2(a) — the "notes like x 5 days" example confused
// (that text belongs on the medicine line itself, not the helper). Ratified
// wording, verbatim.
export const TREATMENT_LINES_HELPER_COPY = 'One medicine per line. Press Enter or use + Add line for the next line. '
  + 'Use Plain instead of Bullet for a heading or a note that should not carry a bullet.';

const BULLET = 'bullet';
const PLAIN = 'plain';

// The FROZEN storage convention (bound to the backend print, do not deviate):
// one string, lines joined by "\n"; a line beginning with "- " is a BULLET
// line, any other non-empty line is PLAIN (heading/note). New lines default
// to BULLET.
const BULLET_PREFIX = '- ';

// A stray "• " glyph is the ONLY leftover from the very first version (before
// FIX-2 introduced the line editor) — stripped once on load. Unlike FIX-2,
// "- " is no longer a stray glyph to scrub: it is now the meaningful bullet
// marker itself, so it is parsed (not discarded) in `parseStoredLines`.
function stripStrayGlyphOnce(line) {
  return line.startsWith('• ') ? line.slice(2) : line;
}

// Typed/pasted text still gets "• "/"- "/"* " scrubbed from the START of the
// characters the user types into a row — unchanged from FIX-2. The bullet/
// plain state of a row is controlled by the toggle, never by what a line's
// text happens to start with, so none of these are meaningful once inside a
// row's own text.
const TYPED_STRAY_PREFIXES = ['• ', '- ', '* '];
function stripTypedStrayPrefixOnce(line) {
  const prefix = TYPED_STRAY_PREFIXES.find((p) => line.startsWith(p));
  return prefix ? line.slice(prefix.length) : line;
}

// Stored/loaded lines -> rows with a mode. LEGACY READ RULE: data saved
// before FIX-3 carries no "- " prefixes at all — if NO line starts with
// "- ", every line is a bullet (and gets the prefix back on next save).
// Otherwise each line is read on its own: "- " => bullet (prefix stripped),
// anything else non-blank => plain.
function parseStoredLines(raw) {
  if (!raw) return [];
  const lines = raw
    .split('\n')
    .map(stripStrayGlyphOnce)
    .filter((line) => line.trim() !== '');
  if (lines.length === 0) return [];

  const hasExplicitMarker = lines.some((line) => line.startsWith(BULLET_PREFIX));
  if (!hasExplicitMarker) {
    return lines.map((text) => ({ mode: BULLET, text }));
  }
  return lines.map((line) => (
    line.startsWith(BULLET_PREFIX)
      ? { mode: BULLET, text: line.slice(BULLET_PREFIX.length) }
      : { mode: PLAIN, text: line }
  ));
}

function toExternalValue(rows) {
  const lines = rows
    .filter((r) => r.text.trim() !== '')
    .map((r) => (r.mode === PLAIN ? r.text : `${BULLET_PREFIX}${r.text}`));
  return lines.length > 0 ? lines.join('\n') : null;
}

function isBulletToggleShortcut(e) {
  return (e.ctrlKey || e.metaKey) && e.shiftKey && (e.key === '8' || e.code === 'Digit8');
}

/**
 * BulletLinesField (DISCHARGE-TEMPLATES Phase 1 · FIX-2 line editor, FIX-3
 * per-line bullet/plain toggle, FIX-6 labelled affordances) — replaces the
 * bullet-glyph textarea for `discharge_medications`.
 *
 * FIX-6 (owner's live-test bug report: rows showed a bare "•"/"–" toggle and
 * nothing else — "not clear how to go to the next line; not clear how to
 * give a bullet and how not") replaces the bare glyph with a labelled chip
 * ("• Bullet" / "Plain"), adds an explicit "×" remove button per row and a
 * "+ Add line" button below the rows, and folds the two separate helper
 * lines into one sentence (`TREATMENT_LINES_HELPER_COPY`). No storage or
 * FIX-3 bullet/plain semantics changed — affordances only.
 *
 * FIX-2 established one row per line so Enter/Backspace/paste are ordinary
 * line-editor operations instead of a caret fight with a whole textarea's
 * value. FIX-3 fixes the follow-on owner bug: the printed page showed EVERY
 * line as a bullet, including the "Rx" heading and the "X5 Days" trailer,
 * because the bullet was purely a static visual cell with no per-line state
 * to print from. Ruling: the doctor chooses per line.
 *
 * Each row now also carries a `mode` (`'bullet'` | `'plain'`), toggled by a
 * real button beside the row (not a static span) — `aria-pressed` reflects
 * bullet-on, and Ctrl/Cmd+Shift+8 toggles the focused row's mode without
 * leaving the keyboard. Storage stays the FROZEN single "\n"-joined string
 * (unchanged wire shape from ruling B/FIX-2) but a bullet line is now
 * written with a leading `"- "` marker so the backend print can tell bullet
 * from plain; a plain line has no marker. `toExternalValue` is the one
 * place this is computed. LEGACY READ RULE: a value saved before this
 * change carries no `"- "` markers at all — on load, if NO line starts with
 * `"- "`, every line is treated as a bullet (matches what it always printed
 * as before FIX-3) and gets the marker back on the next save; otherwise
 * each line is read on its own (see `parseStoredLines`).
 *
 * Controlled contract unchanged: `value` is the plain stored string (or
 * `null`); `onChange(nextValue)` is the only way the parent learns of an
 * edit. Internal `rows` always keeps AT LEAST one row (default mode
 * BULLET, matching "new lines default to BULLET") so there is always
 * something to type into.
 *
 * Re-seed rule (mirrors `InvestigationRowsField`, unchanged from FIX-2):
 * `rows` is only rebuilt from `value` when `value` is NOT the same value
 * this component itself last emitted.
 *
 * Enter — splits the current line at the caret; the NEW row inherits the
 * CURRENT row's mode (so continuing a bulleted list with Enter keeps
 * bulleting, and continuing a plain note keeps it plain, without an extra
 * click). Backspace/paste semantics are unchanged from FIX-2: Backspace on
 * an EMPTY line (never the first) removes it and focuses the end of the
 * previous line; a paste with newlines is split into rows, each stripped of
 * a stray bullet/dash/asterisk prefix, defaulting to BULLET mode (the
 * general "new lines default to bullet" rule — paste isn't "continuing" any
 * single existing row the way Enter is).
 */
export default function BulletLinesField({ value, onChange, disabled = false, label, maxLength, required = false, error }) {
  const idRef = useRef(0);
  const makeId = () => {
    idRef.current += 1;
    return `bl-${idRef.current}`;
  };

  const seedRows = (raw) => {
    const lines = parseStoredLines(raw);
    return (lines.length > 0 ? lines : [{ mode: BULLET, text: '' }]).map((l) => ({ id: makeId(), mode: l.mode, text: l.text }));
  };

  const [rows, setRows] = useState(() => seedRows(value));
  const lastEmittedRef = useRef(value ?? null);
  const pendingFocusRef = useRef(null); // { id, caret }
  const inputRefs = useRef({});
  // QA-verified fix (DISCHARGE-TEMPLATES Phase 1 round-1 defect, DEF-1): the
  // mount render's OWN copy of this effect can be deferred by React past a
  // same-tick edit (confirmed via instrumentation — the parent's async load
  // + this row-editor's synchronous `commit()` can interleave so the mount
  // effect finally runs AFTER `lastEmittedRef` has already moved past the
  // user's first edit, reads a stale mount `value` against the now-advanced
  // ref, wrongly concludes "external reset", and reseeds from the ORIGINAL
  // loaded value — silently discarding the row the user is mid-edit on.
  // `rows` is already correctly seeded once by the `useState` initializer
  // above, so the mount run of this effect is a pure no-op in the
  // well-behaved case anyway; skipping it outright removes the race with no
  // loss of behaviour.
  const didMountRef = useRef(false);

  // Genuine external reset only (never our own echo) — see JSDoc above.
  useEffect(() => {
    if (!didMountRef.current) {
      didMountRef.current = true;
      return;
    }
    if (value !== lastEmittedRef.current) {
      setRows(seedRows(value));
      lastEmittedRef.current = value ?? null;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  useEffect(() => {
    if (!pendingFocusRef.current) return;
    const { id, caret } = pendingFocusRef.current;
    pendingFocusRef.current = null;
    const el = inputRefs.current[id];
    if (el) {
      el.focus();
      el.setSelectionRange(caret, caret);
    }
  }, [rows]);

  const commit = (nextRows) => {
    setRows(nextRows);
    const ext = toExternalValue(nextRows);
    lastEmittedRef.current = ext;
    onChange(ext);
  };

  const handleChange = (id, raw) => {
    const next = stripTypedStrayPrefixOnce(raw);
    commit(rows.map((r) => (r.id === id ? { ...r, text: next } : r)));
  };

  const toggleMode = (id) => {
    commit(rows.map((r) => (r.id === id ? { ...r, mode: r.mode === BULLET ? PLAIN : BULLET } : r)));
  };

  const handleKeyDown = (idx, e) => {
    const row = rows[idx];
    if (isBulletToggleShortcut(e)) {
      e.preventDefault();
      toggleMode(row.id);
      return;
    }
    if (e.key === 'Enter') {
      e.preventDefault();
      const caret = e.target.selectionStart ?? row.text.length;
      const before = row.text.slice(0, caret);
      const after = row.text.slice(caret);
      const newRow = { id: makeId(), mode: row.mode, text: after };
      const next = [...rows.slice(0, idx), { ...row, text: before }, newRow, ...rows.slice(idx + 1)];
      pendingFocusRef.current = { id: newRow.id, caret: 0 };
      commit(next);
      return;
    }
    if (e.key === 'Backspace' && row.text === '' && idx > 0) {
      e.preventDefault();
      const prevRow = rows[idx - 1];
      const next = rows.filter((_, i) => i !== idx);
      pendingFocusRef.current = { id: prevRow.id, caret: prevRow.text.length };
      commit(next);
    }
  };

  // FIX-6 explicit "×" remove control — never a native `confirm()` (a filled
  // line removal needs no confirmation dialog, the doctor can re-add). The
  // one-row invariant used throughout this component (there is always at
  // least one row to type into) is preserved: removing the LAST remaining
  // row just clears its text instead of deleting the row.
  const removeLine = (idx) => {
    if (rows.length === 1) {
      commit(rows.map((r) => ({ ...r, text: '' })));
      pendingFocusRef.current = { id: rows[0].id, caret: 0 };
      return;
    }
    const next = rows.filter((_, i) => i !== idx);
    const focusRow = next[Math.min(idx, next.length - 1)];
    pendingFocusRef.current = { id: focusRow.id, caret: focusRow.text.length };
    commit(next);
  };

  // FIX-6 explicit "+ Add line" affordance — Enter still works; this is the
  // same "append a bullet-mode row" behaviour as Enter on the last line,
  // exposed as a button so it's discoverable without the keyboard.
  const addLine = () => {
    const newRow = { id: makeId(), mode: BULLET, text: '' };
    pendingFocusRef.current = { id: newRow.id, caret: 0 };
    commit([...rows, newRow]);
  };

  const handlePaste = (idx, e) => {
    const text = e.clipboardData ? e.clipboardData.getData('text') : '';
    if (!text.includes('\n')) return; // single line — normal onChange handles it
    e.preventDefault();
    const pasted = text.split('\n').map(stripTypedStrayPrefixOnce).filter((line) => line.trim() !== '');
    const insertRows = (pasted.length > 0 ? pasted : ['']).map((t) => ({ id: makeId(), mode: BULLET, text: t }));
    const next = [...rows.slice(0, idx), ...insertRows, ...rows.slice(idx + 1)];
    const lastInserted = insertRows[insertRows.length - 1];
    pendingFocusRef.current = { id: lastInserted.id, caret: lastInserted.text.length };
    commit(next);
  };

  const displayLabel = required ? `${label} (required)` : label;

  if (disabled) {
    const lines = rows.filter((r) => r.text.trim() !== '');
    return (
      <div data-testid="bullet-lines-field">
        {label && <label className={labelCls}>{displayLabel}</label>}
        {lines.length === 0 ? (
          <div className={readonlyValueCls}>—</div>
        ) : (
          <div className="space-y-1" data-testid="bullet-lines-readonly">
            {lines.map((row, idx) => (
              <div key={idx} className={`flex items-start gap-2 ${readonlyValueCls}`} data-testid={`bullet-line-readonly-${idx}`}>
                {row.mode === BULLET ? (
                  <span className={readonlyBulletCls} aria-hidden="true">&bull;</span>
                ) : (
                  <span className={readonlyPlainCls} aria-hidden="true" />
                )}
                <span>{row.text}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }

  return (
    <div data-testid="bullet-lines-field">
      {label && <label className={labelCls}>{displayLabel}</label>}
      <div className="space-y-1">
        {rows.map((row, idx) => {
          const isBullet = row.mode === BULLET;
          return (
            <div key={row.id} className="flex items-center gap-2" data-testid={`bullet-line-${idx}`}>
              <button
                type="button"
                aria-pressed={isBullet}
                title="Switch this line between a bullet and plain text (e.g. a heading like Rx or a note like x 5 days)"
                onClick={() => toggleMode(row.id)}
                className={`${chipBaseCls} ${isBullet ? chipOnCls : chipOffCls}`}
                data-testid={`bullet-toggle-${idx}`}
              >
                {isBullet ? '• Bullet' : 'Plain'}
              </button>
              <input
                ref={(el) => { inputRefs.current[row.id] = el; }}
                type="text"
                className={`${inputCls} flex-1`}
                placeholder="Type a medicine…"
                maxLength={maxLength || undefined}
                value={row.text}
                onChange={(e) => handleChange(row.id, e.target.value)}
                onKeyDown={(e) => handleKeyDown(idx, e)}
                onPaste={(e) => handlePaste(idx, e)}
                data-testid={`bullet-line-input-${idx}`}
              />
              <button
                type="button"
                aria-label="Remove line"
                title="Remove line"
                onClick={() => removeLine(idx)}
                className={removeBtnCls}
                data-testid={`bullet-line-remove-${idx}`}
              >
                &times;
              </button>
            </div>
          );
        })}
      </div>
      <button
        type="button"
        onClick={addLine}
        className={addLineBtnCls}
        data-testid="bullet-lines-add"
      >
        + Add line
      </button>
      <div className={helperCls}>{TREATMENT_LINES_HELPER_COPY}</div>
      {error && <div className={errorCls} data-testid="bullet-lines-error">{error}</div>}
    </div>
  );
}
